/**
 * Submission Progress Tracking Module
 * Handles PHP session persistence of film submission form data
 */
(function() {
  'use strict';

  class SubmissionProgress {
    constructor () {
      this.session = null;
      this.saveDebounceTimer = null;
      this.autoSaveDelay = 500; // ms
    }

    /**
     * Initialize the submission progress tracking
     */
    init () {
      // Get session from server-side data (passed via window.sflow)
      if (window.sflow && window.sflow.session_data) {
        this.session = window.sflow.session_data;
      }

      // Set up auto-save handlers
      this.setupAutoSave();

      // Expose methods to window for integration
      window.submissionProgress = {
        updateStep: this.updateCurrentStep.bind(this),
        updateFormData: this.updateFormData.bind(this),
        updateImdbId: this.updateImdbId.bind(this),
        clearSession: this.clearSession.bind(this),
        getSession: () => this.session,
      };
    }

    // Methods removed - session is managed server-side via PHP sessions

    /**
     * Set up auto-save handlers for form inputs
     */
    setupAutoSave () {
      const form = document.querySelector('#submitTitleForm, #uploadForm');
      if (!form) return;

      // Listen for input changes
      form.addEventListener('input', (e) => {
        this.debouncedSave();
      });

      form.addEventListener('change', (e) => {
        this.debouncedSave();
      });

      // Listen for Select2 changes
      $(form).on('select2:select select2:unselect', () => {
        this.debouncedSave();
      });
    }

    /**
     * Debounced save function
     */
    debouncedSave () {
      clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = setTimeout(() => {
        this.captureFormData();
        this.saveToServer();
      }, this.autoSaveDelay);
    }

    /**
     * Save form data to server (PHP session)
     */
    saveToServer () {
      if (!this.session || !this.session.form_data) {
        return;
      }

      fetch('/api/submissions/session/save-step', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          step: this.session.current_step || 1,
          data: this.session.form_data
        })
      })
        .then(response => response.json())
        .then(result => {
          if (!result.success) {
            console.error('Failed to save to server:', result.message);
          }
        })
        .catch(error => {
          console.error('Error saving to server:', error);
        });
    }

    /**
     * Capture current form data
     */
    captureFormData () {
      // Check if session exists before attempting to capture data
      if (!this.session) {
        console.log('SubmissionProgress: No session available, skipping form data capture');
        return;
      }

      const form = document.querySelector('#submitTitleForm, #uploadForm');
      if (!form) return;

      const formData = {};

      // Get all form inputs
      const inputs = form.querySelectorAll('input, select, textarea');
      inputs.forEach(input => {
        if (!input.name) return;

        if (input.type === 'checkbox') {
          if (input.name.endsWith('[]')) {
            // Handle array inputs (like platforms[])
            const name = input.name.slice(0, -2);
            if (!formData[name]) {
              formData[name] = [];
            }
            if (input.checked) {
              formData[name].push(input.value);
            }
          } else {
            formData[input.name] = input.checked;
          }
        } else if (input.type === 'radio') {
          if (input.checked) {
            formData[input.name] = input.value;
          }
        } else if (input.type !== 'file' && input.type !== 'submit' && input.type !== 'button') {
          formData[input.name] = input.value;
        }
      });

      this.session.form_data = formData;
    }

    /**
     * Update current step
     */
    updateCurrentStep (step) {
      if (this.session) {
        this.session.current_step = step;
        this.captureFormData();
        this.saveToServer();
      }
    }

    /**
     * Update form data programmatically
     */
    updateFormData (data) {
      if (this.session) {
        this.session.form_data = { ...this.session.form_data, ...data };
        this.saveToServer();
      }
    }

    /**
     * Update IMDB ID when imported
     */
    updateImdbId (imdbId) {
      if (this.session) {
        this.session.imdb_id = imdbId;
        // Also update form data
        if (!this.session.form_data) {
          this.session.form_data = {};
        }
        this.session.form_data.imdb_id = imdbId;
        this.saveToServer();
      }
    }

    /**
     * Navigate to specific step
     */
    navigateToStep (step) {
      // Trigger step navigation if submit-title module is loaded
      if (window.submitTitle && typeof window.submitTitle.goToStep === 'function') {
        window.submitTitle.goToStep(step);
      } else {
        // Fallback: manually show the step
        document.querySelectorAll('.step').forEach(el => {
          el.style.display = 'none';
        });
        const targetStep = document.querySelector(`.step[data-step="${step}"]`);
        if (targetStep) {
          targetStep.style.display = 'block';
        }
      }
    }

    /**
     * Clear session data (PHP session + mark submission deleted)
     */
    clearSession () {
      try {
        const uuid = this.session?.uuid;

        // Clear PHP session and mark submission as deleted via API
        if (uuid) {
          fetch('/api/submissions/session/clear', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              uuid: uuid,
              mark_deleted: true
            })
          })
            .then(response => response.json())
            .then(result => {
              if (result.success) {
                console.log('Session cleared and submission marked as deleted');
                // Reload page to get new session
                window.location.reload();
              } else {
                console.error('Failed to clear server session:', result.message);
              }
            })
            .catch(error => {
              console.error('Error clearing server session:', error);
            });
        }

        this.session = null;
      } catch (e) {
        console.error('Failed to clear submission session:', e);
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const tracker = new SubmissionProgress();
      tracker.init();
    });
  } else {
    const tracker = new SubmissionProgress();
    tracker.init();
  }
})();